import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from "umi";
import { Modal, Button, Table, SearchBox, Transfer, Bubble } from "tea-component";

import {getCountryFlag} from "@/pages/home/_config/countries";
import {WorldClockData} from "@/pages/dashboard/pages/multiTimezone/_config/types";
import {saveWorldClock} from "@/pages/dashboard/services/multiTimezone";

import "@/pages/dashboard/assets/scss/components/navigation/editRelatedGame.scss";

const { selectable, removeable, scrollable, draggable } = Table.addons;

export default function EditRelatedGame(props: any) {
  const {formatMessage} = useIntl();
  const {visible, close, defaultClockDataList, customClockDataList, setCustomClockDataList, gameCode, type} = props;
  const [selectAreaNameList, setSelectAreaNameList] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
    
  useEffect(() => {
    if (visible) {
      setSelectAreaNameList((customClockDataList || []).map((data: WorldClockData) => data.area_code),
      );
      setInputValue("");
    }
  }, [visible]);
    
  const confirm = () => {
    setLoading(true);
    const worldClockDataList = selectAreaNameList.map((name: string) => (props.defaultClockDataList || []).find((i) => i.area_code === name));
    const params = {
      game_code: gameCode,
      type,
      customized: worldClockDataList,
    };
    
    saveWorldClock(params).then(() => {
      setCustomClockDataList(worldClockDataList);
      close();
    }).finally(() => {
      setLoading(false);
    });
  };
    
  const dataSource = defaultClockDataList?.filter((i) => (i.area_show_code ?? i.area_code)?.toLowerCase().includes(inputValue.toLowerCase()));
    
  
  return <Modal disableCloseIcon size="xl" className='editRelatedGame' visible={visible} caption={formatMessage({ id: 'daily_by_time_zone_world_clock_setting' })} onClose={close}>
    <Modal.Body>
      <Transfer
        leftCell={
          <Transfer.Cell
            scrollable={false}
            title={formatMessage({ id: 'daily_by_time_zone_world_clock_setting_defaults' })}
            header={
              <SearchBox
                value={inputValue}
                onChange={value => setInputValue(value)}
                placeholder={formatMessage({ id: 'daily_by_time_zone_world_clock_setting_search' })}
              />
            }
          >
            {
              dataSource?.length ? <SourceTable
                dataSource={dataSource}
                targetKeys={selectAreaNameList}
                onChange={(keys: string[]) => {
                  if (keys.length === 0) {
                    // 全不选时，默认保留一个
                    setSelectAreaNameList((selectAreaNameList: string[]) => selectAreaNameList.filter((i, index) => index === 0));
                  } else {
                    setSelectAreaNameList((selectAreaNameList: string[]) => {
                      keys.forEach((key: string) => {
                        if (!selectAreaNameList.includes(key)) {
                          selectAreaNameList.push(key);
                        }
                      });
                      const list = selectAreaNameList.filter((i) => keys.includes(i));
                      // 选择的总数不能大于24
                      if (list.length > 24) {
                        list.length = 24;
                      }
                      return list;
                    });
                  }
                }}
              /> : <div className='table-select-nodata'>{formatMessage({id: "daily_by_time_zone_world_clock_setting_search_no_result"})}</div>
            }
          </Transfer.Cell>
        }
        rightCell={
          <Transfer.Cell
            title={`${formatMessage({ id: 'daily_by_time_zone_world_clock_setting_customizeds' })} (${selectAreaNameList.length})`}
          >
            <TargetTable
              dataSource={selectAreaNameList.map((name: string) => defaultClockDataList.find((i) => i.area_code === name))}
              targetKeys={selectAreaNameList}
              onDragEnd={(records) => {
                setSelectAreaNameList(records.map((i) => i.area_code));
              }}
              onRemove={(key) => {
                setSelectAreaNameList((selectAreaNameList: string[]) => selectAreaNameList.filter((i) => i !== key));
              }}
            />
          </Transfer.Cell>
        }
      />
    </Modal.Body>
    <Modal.Footer>
      <Button type="primary" onClick={confirm} loading={loading} disabled={loading}>
        {formatMessage({ id: 'daily_by_time_zone_world_clock_setting_confirm' })}
      </Button>
      <Button type="weak" onClick={close}>
        {formatMessage({ id: 'daily_by_time_zone_world_clock_setting_cancel' })}
      </Button>
    </Modal.Footer>
  </Modal>;
};

const BubbleEllipsis = (props: any) => {
  const { content, maxWidth } = props;
  const chartRef = useRef(null);
  const [flag, setFlag] = useState(true);
  useEffect(() => {
    if (chartRef?.current) {
      const box = chartRef.current;
      if (+box.offsetWidth > maxWidth) {
        setFlag(false);
      }
    }
  }, [props]);
  return (
    <div style={{ maxWidth, whiteSpace: 'nowrap',overflow: "hidden", textOverflow: "ellipsis" }}>
      {
        flag ?
          <span ref={chartRef} style={{ display: 'inline-block' }} >{content}</span> :
          <Bubble
            arrowPointAtCenter
            placement="top-start"
            dark
            content={content}
            style={{ left: '-18px' }}
          >
            <div className='tableBox-cont-text'>{content}</div>
          </Bubble>
      }
    </div>
  );
};

const render = (record: WorldClockData) => (
  <div className='tableBox-cont'>
    <img src={getCountryFlag(record.area_code)} className='tableBox-cont-img' style={{ display: record.show_icon ? '' : 'none' }}/>
    <BubbleEllipsis content={record.area_name} maxWidth={150} />
    <span className="transfer-table__itme-time">（{record.time_zone_show}）</span>
  </div>
);


const BubbleTipContent = (targetKeys, rowKey, formatMessage) => {
  if (targetKeys.length <= 1 && targetKeys.includes(rowKey)) {
    return formatMessage({id: 'daily_by_time_zone_world_clock_setting_select_only_one_tip'});
  }
  if (targetKeys.length >= 24 && !targetKeys.includes(rowKey)) {
    return formatMessage({id: 'daily_by_time_zone_world_clock_setting_select_more_than_24_tip'});
  }
  return '';
};

const SourceTable = ({dataSource, targetKeys, onChange}: any) => {
  const {formatMessage} = useIntl();
  const [records, setRecords] = useState(dataSource);
    
  useEffect(() => {
    setRecords(dataSource);
  }, [dataSource]);
  return (<Table
    records={records}
    recordKey="area_code"
    rowDisabled={record => record.status === "stopped"}
    rowClassName={record => record.status}
    columns={[
      {
        key: 'instance',
        header: formatMessage({id: 'daily_by_time_zone_world_clock_setting_select_all'}),
        render,
      },
    ]}
    className="table-select table-select1"
    addons={[
      scrollable({
        virtualizedOptions: {
          height: 478,
          itemHeight: 45,
          onScrollBottom: () => console.log("Bottom"),
        },
      }),
      selectable({
        value: targetKeys,
        onChange,
                    
        rowSelect: true,
        rowSelectable: (rowKey) => {
          if (targetKeys.length <= 1 && targetKeys.includes(rowKey)) {
            return false;
          }
          if (targetKeys.length >= 24 && !targetKeys.includes(rowKey)) {
            return false;
          }
          return true;
        },
        render: (element, context) => (
          <Bubble dark content={BubbleTipContent(targetKeys, context.rowKey, formatMessage)}
            placement={'top'}>
            {element}
          </Bubble>
        ),
      }),
    ]}
  />);
};

const TargetTable = ({dataSource, targetKeys, onDragEnd, onRemove}: any) => {
  const [records, setRecords] = useState(dataSource);
    
  useEffect(() => {
    setRecords(dataSource);
  }, [dataSource]);
  return (<Table
    records={records}
    recordKey="area_code"
    columns={[
      {
        key: 'instance',
        header: '',
        render,
      },
    ]}
    className="table-select table-select2"
    addons={[
      scrollable({
        virtualizedOptions: {
          height: 572,
          itemHeight: 45,
        },
      }),
      removeable({
        onRemove,
        render: (element) => (targetKeys.length > 1 ? element : <></>)
      }),
      draggable({
        // module: rbd,
        onDragEnd,
      }),
    ]}
  />);
};